import { StringContext } from "../../core/StringContext";
import { StringModule } from "../../core/StringModule";
import { StringObject } from "../../objects/StringObject";
import { RuleParserResult } from "../../tools/RuleParserTool";
import type { ValidationContext } from "../../tools/ValidationTool";

type FormField = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

interface RegisteredEvent {
  eventElement: EventTarget;
  eventType: string;
  eventCallback: EventListenerOrEventListenerObject;
}

interface FieldEntry {
  field: FormField;
  key: string;
  rules: RuleParserResult[];
  supportsRealtime: boolean;
  needsContext: boolean;
  inputEventType: "input" | "change";
  inputHandler: EventListener;
  beforeInputHandler?: EventListener;
}

type FormState = {
  object: StringObject;
  form: HTMLFormElement;
  entries: FieldEntry[];
  values: Record<string, any>;
  events: RegisteredEvent[];
};

export class StringForm extends StringModule {
  constructor(context: StringContext) {
    super(context);
    this.htmlKey = "form";
  }

  initializeObject(
    globalId: number,
    object: StringObject,
    element: HTMLElement,
    attributes: Record<string, any>
  ): void {
    super.initializeObject(globalId, object, element, attributes);

    const existingEvents = object.getProperty<Array<RegisteredEvent>>("form-events") ?? [];
    existingEvents.forEach((evt) => {
      evt.eventElement.removeEventListener(evt.eventType, evt.eventCallback);
    });
    existingEvents.length = 0;
    object.setProperty("form-events", existingEvents);

    super.onObjectConnected(object);
    const form = object.htmlElement as HTMLFormElement;

    const fieldEntries: FieldEntry[] = [];
    const fieldValues: Record<string, any> = {};

    this.getInteractiveFields(form).forEach((field, idx) =>
      this.registerField(field, form, fieldEntries, fieldValues, existingEvents, idx)
    );

    const submitCallback = (event: Event) => {
      event.preventDefault();
      let allValid = true;
      const data: Record<string, any> = {};
      const processedRadioKeys = new Set<string>();
      for (const entry of fieldEntries) {
        const field = entry.field;
        if (!field.isConnected || !this.shouldValidateField(field)) continue;
        if (this.isRadioField(field)) {
          if (processedRadioKeys.has(entry.key)) {
            continue;
          }
          processedRadioKeys.add(entry.key);
        }
        const { key, rules, needsContext } = entry;
        const value = this.getFieldValue(field);
        data[key] = value;
        fieldValues[key] = value;

        const { valid, errors } = this.tools.validation.process({
          rules,
          value,
          context: this.buildContext(needsContext, key, fieldValues),
        });

        this.applyValidationState(form, field, key, valid, errors, "submit");

        if (!valid) {
          allValid = false;
        }
      }

      if (allValid) {
        this.events.emit(`form:submit:${object.id}`, data);
      } else {
        const processedRadioKeys = new Set<string>();
        const firstInvalidEntry = fieldEntries.find((entry) => {
          const field = entry.field;
          if (!field.isConnected || !this.shouldValidateField(field)) return false;
          if (this.isRadioField(field)) {
            if (processedRadioKeys.has(entry.key)) {
              return false;
            }
            processedRadioKeys.add(entry.key);
          }
          const { key, rules, needsContext } = entry;
          const value = this.getFieldValue(field);
          fieldValues[key] = value;
          const { valid } = this.tools.validation.process({
            rules,
            value,
            context: this.buildContext(needsContext, key, fieldValues),
          });
          return !valid;
        });
        if (firstInvalidEntry?.field && typeof firstInvalidEntry.field.focus === "function") {
          firstInvalidEntry.field.focus();
        }
        this.events.emit(`form:invalid:${object.id}`);
      }
    };

    form.addEventListener("submit", submitCallback);
    existingEvents.push({ eventElement: form, eventType: "submit", eventCallback: submitCallback });

    object.setProperty("form-field-entries", fieldEntries);
    object.setProperty("form-field-values", fieldValues);
  }

  onObjectConnected(object: StringObject) {}

  onDOMMutate(added: NodeList, removed: NodeList): void {
    if (this.objects.length === 0) {
      return;
    }

    if (added.length > 0) {
      this.handleMutationAdditions(added);
    }

    if (removed.length > 0) {
      this.handleMutationRemovals(removed);
    }
  }

  private applyValidationState(
    form: HTMLFormElement,
    field: FormField,
    key: string,
    valid: boolean,
    errors: string[],
    phase: "live" | "submit"
  ): void {
    const errorBlock = form.querySelector(`[string-input="error[${key}]"]`);
    const groupBlock = form.querySelector(`[string-input="group[${key}]"]`);
    if (errorBlock) {
      errorBlock.innerHTML = "";
      errors.forEach((message) => {
        const span = document.createElement("span");
        span.textContent = message;
        errorBlock.appendChild(span);
      });
    }
    if (phase === "live") {
      field.classList.toggle("-invalid", !valid);
      field.classList.remove("-error");
    } else {
      field.classList.remove("-invalid");
      field.classList.toggle("-error", !valid);
    }
    field.classList.toggle("-valid", valid);
    if (groupBlock) {
      if (phase === "live") {
        groupBlock.classList.toggle("-invalid", !valid);
        groupBlock.classList.remove("-error");
      } else {
        groupBlock.classList.remove("-invalid");
        groupBlock.classList.toggle("-error", !valid);
      }
      groupBlock.classList.toggle("-valid", valid);
    }

    const eventSuffix = valid ? "valid" : phase === "live" ? "invalid" : "error";
    this.events.emit(`form:field:${eventSuffix}:${key}`, {
      key,
      field,
      errors,
      phase,
      valid,
    });
  }

  private getInteractiveFields(form: HTMLFormElement): FormField[] {
    return Array.from(form.querySelectorAll("[string-input]"))
      .filter((el) => !this.isServiceFieldAttribute(el.getAttribute("string-input") || ""))
      .filter((el): el is FormField => this.isFormFieldElement(el))
      .map((el) => el as FormField);
  }

  private getFieldRules(field: FormField): RuleParserResult[] {
    const ruleString =
      this.tools.domAttribute.process({
        element: field,
        key: "input",
      }) ?? "";
    return this.tools.ruleParser.process({ value: ruleString });
  }

  private registerField(
    field: FormField,
    form: HTMLFormElement,
    fieldEntries: FieldEntry[],
    fieldValues: Record<string, any>,
    events: RegisteredEvent[],
    preferredIndex?: number
  ): void {
    if (!this.isFormFieldElement(field)) return;
    if (field.closest("form") !== form) return;
    if (fieldEntries.some((entry) => entry.field === field)) return;

    const registeredIndex = this.registerFieldIndex(field, preferredIndex ?? fieldEntries.length);
    const key = this.getInputKey(field, registeredIndex);
    const rules = this.getFieldRules(field);
    const supportsRealtime = this.supportsBeforeInputValidation(rules);
    const needsContext = this.requiresContext(rules);
    const inputEventType = this.getInputEventType(field);

    const entry: FieldEntry = {
      field,
      key,
      rules,
      supportsRealtime,
      needsContext,
      inputEventType,
      inputHandler: () => undefined,
    };

    const inputHandler = (event: Event) => {
      const target = (event.currentTarget || event.target) as FormField | null;
      if (!target || !target.isConnected || !this.shouldValidateField(target)) {
        return;
      }
      const value = this.getFieldValue(target);
      fieldValues[entry.key] = value;
      const context = this.buildContext(entry.needsContext, entry.key, fieldValues);

      const { valid, errors } = this.tools.validation.process({
        rules: entry.rules,
        value,
        context,
      });

      this.applyValidationState(form, target, entry.key, valid, errors, "live");
    };

    entry.inputHandler = inputHandler;
    field.addEventListener(inputEventType, inputHandler);
    events.push({ eventElement: field, eventType: inputEventType, eventCallback: inputHandler });

    if (
      supportsRealtime &&
      (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)
    ) {
      const beforeInputHandler = (event: Event) => {
        const inputEvent = event as InputEvent;
        if (inputEvent.isComposing || inputEvent.inputType?.startsWith("insertComposition")) {
          return;
        }
        const target = (event.currentTarget || event.target) as FormField | null;
        if (
          !target ||
          !(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) ||
          !target.isConnected
        ) {
          return;
        }

        const start = target.selectionStart ?? 0;
        const end = target.selectionEnd ?? 0;
        let nextVal = target.value;

        switch (inputEvent.inputType) {
          case "deleteContentBackward":
            nextVal =
              start === end && start > 0
                ? target.value.slice(0, start - 1) + target.value.slice(end)
                : target.value.slice(0, start) + target.value.slice(end);
            break;
          case "deleteContentForward":
            nextVal =
              start === end && start < target.value.length
                ? target.value.slice(0, start) + target.value.slice(start + 1)
                : target.value.slice(0, start) + target.value.slice(end);
            break;
          case "insertFromPaste":
          case "insertFromDrop":
          case "insertReplacementText":
            nextVal =
              target.value.slice(0, start) + (inputEvent.data || "") + target.value.slice(end);
            break;
          default:
            if (typeof inputEvent.data === "string") {
              nextVal = target.value.slice(0, start) + inputEvent.data + target.value.slice(end);
            }
        }

        const { errors } = this.tools.validation.process({
          rules: entry.rules,
          value: nextVal,
          type: "beforeinput",
          context: this.buildContext(entry.needsContext, entry.key, fieldValues, {
            applied: true,
            value: nextVal,
          }),
        });

        if (errors.length > 0 && event.cancelable) {
          event.preventDefault();
        }
      };

      entry.beforeInputHandler = beforeInputHandler;
      field.addEventListener("beforeinput", beforeInputHandler);
      events.push({
        eventElement: field,
        eventType: "beforeinput",
        eventCallback: beforeInputHandler,
      });
    }

    field.classList.add("-inited");
    fieldEntries.push(entry);
    fieldValues[key] = this.getFieldValue(field);
  }

  private unregisterField(
    field: FormField,
    fieldEntries: FieldEntry[],
    fieldValues: Record<string, any>,
    events: RegisteredEvent[]
  ): void {
    const index = fieldEntries.findIndex((entry) => entry.field === field);
    if (index === -1) return;
    const entry = fieldEntries[index];
    if (entry.inputHandler) {
      field.removeEventListener(entry.inputEventType, entry.inputHandler);
    }
    if (entry.beforeInputHandler) {
      field.removeEventListener("beforeinput", entry.beforeInputHandler);
    }
    delete fieldValues[entry.key];
    fieldEntries.splice(index, 1);
    for (let i = events.length - 1; i >= 0; i--) {
      const registered = events[i];
      if (registered.eventElement !== field) continue;
      if (
        registered.eventCallback === entry.inputHandler ||
        (entry.beforeInputHandler && registered.eventCallback === entry.beforeInputHandler)
      ) {
        events.splice(i, 1);
      }
    }
    field.classList.remove("-inited");
  }

  private collectInteractiveFieldsFromNode(node: Node): FormField[] {
    const elements: Element[] = [];
    if (node instanceof Element) {
      if (node.hasAttribute("string-input")) {
        elements.push(node);
      }
      elements.push(...Array.from(node.querySelectorAll("[string-input]")));
    } else if (node instanceof DocumentFragment) {
      elements.push(...Array.from(node.querySelectorAll("[string-input]")));
    }

    return elements
      .filter((el) => !this.isServiceFieldAttribute(el.getAttribute("string-input") || ""))
      .filter((el): el is FormField => this.isFormFieldElement(el));
  }

  private isRadioField(field: FormField): field is HTMLInputElement {
    return field instanceof HTMLInputElement && field.type === "radio";
  }

  private handleMutationAdditions(nodes: NodeList): void {
    nodes.forEach((node) => {
      const fields = this.collectInteractiveFieldsFromNode(node);
      fields.forEach((field) => {
        const state = this.getFormStateByContainment(field);
        if (!state) return;
        this.registerField(field, state.form, state.entries, state.values, state.events);
      });
    });
  }

  private handleMutationRemovals(nodes: NodeList): void {
    nodes.forEach((node) => {
      const fields = this.collectInteractiveFieldsFromNode(node);
      fields.forEach((field) => {
        const state = this.getFormStateByReference(field);
        if (!state) return;
        this.unregisterField(field, state.entries, state.values, state.events);
      });
    });
  }

  private getFormStateByContainment(field: FormField): FormState | null {
    const owner = this.objects.find(
      (object) =>
        object.htmlElement instanceof HTMLFormElement && object.htmlElement.contains(field)
    );
    if (!owner) return null;
    return this.buildFormState(owner);
  }

  private getFormStateByReference(field: FormField): FormState | null {
    for (const object of this.objects) {
      const entries = object.getProperty<FieldEntry[]>("form-field-entries");
      if (!entries) continue;
      if (entries.some((entry) => entry.field === field)) {
        return this.buildFormState(object, entries);
      }
    }
    return null;
  }

  private buildFormState(object: StringObject, entries?: FieldEntry[]): FormState | null {
    const element = object.htmlElement;
    if (!(element instanceof HTMLFormElement)) {
      return null;
    }
    const stateEntries = entries ?? object.getProperty<FieldEntry[]>("form-field-entries");
    const values = object.getProperty<Record<string, any>>("form-field-values");
    const events = object.getProperty<Array<RegisteredEvent>>("form-events");
    if (!stateEntries || !values || !events) {
      return null;
    }
    return {
      object,
      form: element,
      entries: stateEntries,
      values,
      events,
    };
  }

  private registerFieldIndex(field: FormField, idx: number): number {
    const existing = field.getAttribute("data-string-form-index");
    if (existing !== null) {
      return Number(existing);
    }
    field.setAttribute("data-string-form-index", String(idx));
    return idx;
  }

  private getFieldIndex(field: FormField, fallbackIdx: number): number {
    const existing = field.getAttribute("data-string-form-index");
    if (existing !== null) {
      const parsed = Number(existing);
      return Number.isNaN(parsed) ? fallbackIdx : parsed;
    }
    return this.registerFieldIndex(field, fallbackIdx);
  }

  private shouldValidateField(field: FormField): boolean {
    if (field.disabled) return false;
    if (field instanceof HTMLInputElement && field.type === "hidden") return false;
    return true;
  }

  private supportsBeforeInputValidation(rules: RuleParserResult[]): boolean {
    return rules.some((rule) => StringForm.beforeInputRuleKeys.has(rule.key));
  }

  private requiresContext(rules: RuleParserResult[]): boolean {
    return rules.some((rule) => StringForm.crossFieldRuleKeys.has(rule.key));
  }

  private buildContext(
    needsContext: boolean,
    fieldKey: string,
    fieldValues: Record<string, any>,
    override?: { applied: boolean; value: any }
  ): ValidationContext {
    if (!needsContext) {
      return { fieldKey };
    }

    const needsOverride = Boolean(override?.applied);
    const values = needsOverride ? { ...fieldValues, [fieldKey]: override!.value } : fieldValues;

    return {
      fieldKey,
      values,
      getValue: (lookupKey: string) => {
        if (needsOverride && lookupKey === fieldKey) {
          return override!.value;
        }
        return values[lookupKey];
      },
    };
  }

  private static readonly beforeInputRuleKeys = new Set([
    "number",
    "integer",
    "email",
    "phone",
    "letters",
    "lettersSpaces",
    "lettersNumbers",
    "alpha",
    "alpha_num",
    "alpha_dash",
    "digits",
    "url",
    "pattern",
  ]);

  private static readonly crossFieldRuleKeys = new Set(["same", "different", "after", "before"]);

  private static readonly serviceAttributePrefixes = ["error", "group"];

  getInputKey(field: FormField, idx: number): string {
    return (
      this.tools.domAttribute.process({
        element: field,
        key: "id",
      }) ||
      field.getAttribute("name") ||
      field.getAttribute("id") ||
      `input-${idx}`
    );
  }

  getFieldValue(field: FormField): any {
    if (field instanceof HTMLInputElement) {
      if (field.type === "checkbox") {
        if (field.name) {
          const form = field.form || field.closest("form");
          const checked = form
            ? Array.from(
                form.querySelectorAll<HTMLInputElement>(
                  `input[type="checkbox"][name="${field.name}"]:checked`
                )
              )
            : [field];
          if (checked.length > 1) return checked.map((el) => el.value);
          return checked.length === 1 ? checked[0].value : "";
        }
        return field.checked;
      }
      if (field.type === "radio") {
        if (field.name) {
          const form = field.form || field.closest("form");
          const checked = form?.querySelector<HTMLInputElement>(
            `input[type="radio"][name="${field.name}"]:checked`
          );
          return checked ? checked.value : "";
        }
        return field.checked ? field.value : "";
      }
      if (field.type === "file") {
        if (field.files && field.files.length > 0) {
          return field.multiple ? Array.from(field.files) : field.files[0];
        }
        return field.value;
      }
      return field.value;
    }
    if (field instanceof HTMLSelectElement) {
      if (field.multiple) {
        return Array.from(field.selectedOptions).map((opt) => opt.value);
      }
      return field.value;
    }
    if (field instanceof HTMLTextAreaElement) {
      return field.value;
    }
    return "";
  }

  private isServiceFieldAttribute(attr: string): boolean {
    return StringForm.serviceAttributePrefixes.some((type) => attr.startsWith(`${type}[`));
  }

  private isFormFieldElement(element: Element): element is FormField {
    return (
      element instanceof HTMLInputElement ||
      element instanceof HTMLSelectElement ||
      element instanceof HTMLTextAreaElement
    );
  }

  private getInputEventType(field: FormField): "input" | "change" {
    if (
      field instanceof HTMLSelectElement ||
      (field instanceof HTMLInputElement && (field.type === "checkbox" || field.type === "radio"))
    ) {
      return "change";
    }
    return "input";
  }
}
